import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable, inject } from "@angular/core";
import { ENVIRONMENT } from "@kiraah-shared/environment";
import type {
  IAccessibleTenantInfo,
  IMfaVerificationRequest,
  ISelectTenantRequest,
} from "@kiraah-shared/common";
import { LvfAppModule, LvfAppPlatform, LvfOperationType, LvfRequestSource } from "@kiraah-shared/common";
import { firstValueFrom } from "rxjs";
import { MOBILE_GATEWAY_URL } from "./mobile-auth.config";
import { IMobileAuthService } from "./mobile-auth.service.interface";
import {
  LogoutRequest,
  MobileApiResponse,
  MobileAuthResult,
  MobileLoginRequest,
  OtpLoginRequest,
  OtpLoginRequestResult,
  OtpLoginVerifyRequest,
  TokenPairInfo,
} from "../models/mobile-auth.models";

function ensureApiSuccess<T>(response: MobileApiResponse<T>): T {
  if (!response?.IsSuccessful || response.Data == null) {
    throw new Error(response?.Message ?? "Mobile authentication API error");
  }

  return response.Data;
}

@Injectable()
export class MobileAuthHttpService implements IMobileAuthService {
  private readonly authBaseUrl: string;
  private readonly environmentInfo = inject(ENVIRONMENT);

  constructor(
    private readonly httpClient: HttpClient,
    @Inject(MOBILE_GATEWAY_URL) gatewayUrl: string
  ) {
    this.authBaseUrl = `${gatewayUrl}/api/master/authentication`;
  }

  private buildAuthHeaders(operationType: LvfOperationType = LvfOperationType.UNKNOWN): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json",
      "X-APP-MODULE-UID": `${LvfAppModule.APPLICATION}`,
      "X-REQUEST-SRC": `${LvfRequestSource.MOBILE}`,
      "X-OPERATION-TYPE": `${operationType}`,
      "X-ENVIRONMENT": `${this.environmentInfo.Environment ?? ""}`,
      "X-WEB-APP-URL": `${this.environmentInfo.WebAppUrl ?? ""}`,
    });
  }

  private get authHttpOptions() {
    return {
      headers: this.buildAuthHeaders(),
      withCredentials: true,
    };
  }

  async login(data: MobileLoginRequest): Promise<MobileAuthResult> {
    const response = await firstValueFrom(
      this.httpClient.post<MobileApiResponse<MobileAuthResult>>(
        `${this.authBaseUrl}/login`,
        data,
        this.authHttpOptions
      )
    );

    return ensureApiSuccess(response);
  }

  async verifyMfa(data: IMfaVerificationRequest): Promise<MobileAuthResult> {
    const response = await firstValueFrom(
      this.httpClient.post<MobileApiResponse<MobileAuthResult>>(
        `${this.authBaseUrl}/mfa/verify`,
        data,
        this.authHttpOptions
      )
    );

    return ensureApiSuccess(response);
  }

  async selectTenant(data: ISelectTenantRequest): Promise<MobileAuthResult> {
    const response = await firstValueFrom(
      this.httpClient.post<MobileApiResponse<MobileAuthResult>>(
        `${this.authBaseUrl}/select-tenant`,
        data,
        this.authHttpOptions
      )
    );

    return ensureApiSuccess(response);
  }

  async requestOtpLogin(data: OtpLoginRequest): Promise<OtpLoginRequestResult> {
    const response = await firstValueFrom(
      this.httpClient.post<MobileApiResponse<OtpLoginRequestResult>>(
        `${this.authBaseUrl}/otp-login/request`,
        data,
        this.authHttpOptions
      )
    );

    return ensureApiSuccess(response);
  }

  async verifyOtpLogin(data: OtpLoginVerifyRequest): Promise<MobileAuthResult> {
    const response = await firstValueFrom(
      this.httpClient.post<MobileApiResponse<MobileAuthResult>>(
        `${this.authBaseUrl}/otp-login/verify`,
        data,
        this.authHttpOptions
      )
    );

    return ensureApiSuccess(response);
  }

  async me(): Promise<MobileAuthResult> {
    const headers = this.buildAuthHeaders().set(
      "X-REQUESTED-PLATFORM",
      String(LvfAppPlatform.MOBILE)
    );
    const response = await firstValueFrom(
      this.httpClient.get<MobileApiResponse<MobileAuthResult>>(`${this.authBaseUrl}/me`, {
        headers,
        withCredentials: true,
      })
    );

    return ensureApiSuccess(response);
  }

  async getAccessibleTenants(): Promise<IAccessibleTenantInfo[]> {
    const response = await firstValueFrom(
      this.httpClient.get<MobileApiResponse<IAccessibleTenantInfo[]>>(
        `${this.authBaseUrl}/accessible-tenants`,
        {
          ...this.authHttpOptions,
          headers: this.buildAuthHeaders(LvfOperationType.READ_ALL),
        }
      )
    );

    return ensureApiSuccess(response);
  }

  async refreshToken(): Promise<TokenPairInfo> {
    const response = await firstValueFrom(
      this.httpClient.post<MobileApiResponse<TokenPairInfo>>(
        `${this.authBaseUrl}/refresh`,
        {},
        this.authHttpOptions
      )
    );

    return ensureApiSuccess(response);
  }

  async logout(identityUid: string, request?: LogoutRequest): Promise<void> {
    await firstValueFrom(
      this.httpClient.post<MobileApiResponse<boolean>>(
        `${this.authBaseUrl}/logout/${encodeURIComponent(identityUid)}`,
        {
          RevokeAll: request?.RevokeAll ?? false,
          RefreshToken: request?.RefreshToken,
        },
        this.authHttpOptions
      )
    );
  }
}
